import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth, hasPermission, getRoleLabel, getRoleColor, UserRole } from "@/contexts/AuthContext";
import {
  BarChart3, Users, Send, Wand2, PenSquare, Sparkles, Package, MessageSquare, Settings, LogOut, Menu, X, UsersRound, Languages, GraduationCap, Clock
} from "lucide-react";
import { useState } from "react";
import "./Layout.css";

interface NavItem {
  to: string;
  icon: typeof BarChart3;
  label: string;
  requiredRole?: UserRole[];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", icon: BarChart3, label: "Dashboard" },
  { to: "/customers", icon: Users, label: "Customers" },
  { to: "/follow-ups", icon: Send, label: "Follow-Up" },
  { to: "/room-rebuild", icon: Wand2, label: "Room Rebuild" },
  { to: "/create-post", icon: PenSquare, label: "Create Post" },
  { to: "/fred", icon: Sparkles, label: "Furniture Fred" },
  // { to: "/products", icon: Package, label: "Products" },
  { to: "/messages", icon: MessageSquare, label: "Messages" },
  { to: "/translator", icon: Languages, label: "Translator" },
  { to: "/training", icon: GraduationCap, label: "Training" },
  { to: "/team", icon: UsersRound, label: "Team", requiredRole: ["admin"] },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const { user, logout, trialStatus } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const initials = user?.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "?";

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.requiredRole) return true;
    return user?.role && item.requiredRole.includes(user.role);
  });

  const roleLabel = user?.role ? getRoleLabel(user.role) : "";
  const roleColor = user?.role ? getRoleColor(user.role) : "";

  return (
    <div className="layout">
      <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <Sparkles size={24} />
            {!collapsed && <span>Showroom AiPro</span>}
          </div>
          <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
        </div>
        <nav className="sidebar-nav">
          {visibleItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              title={collapsed ? label : undefined}
            >
              <Icon size={20} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          {trialStatus.isOnTrial && !collapsed && (
            <div className="trial-sidebar-banner">
              <Clock size={14} />
              <div>
                <strong>{trialStatus.daysLeft} day{trialStatus.daysLeft !== 1 ? "s" : ""} left</strong>
                <span>on free trial</span>
              </div>
            </div>
          )}
          {trialStatus.isOnTrial && collapsed && (
            <div className="trial-sidebar-dot" title={`${trialStatus.daysLeft} days left on trial`}>
              {trialStatus.daysLeft}
            </div>
          )}
          <div className="sidebar-user">
            <div className="user-avatar-sm">{initials}</div>
            {!collapsed && (
              <div className="user-info-sm">
                <div className="user-name-sm">{user?.name}</div>
                <div className="user-role-badge" style={{ background: `${roleColor}20`, color: roleColor }}>
                  {roleLabel}
                </div>
              </div>
            )}
          </div>
          <button className="nav-item logout-btn" onClick={handleLogout} title="Log out">
            <LogOut size={20} />
            {!collapsed && <span>Log Out</span>}
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
